import { MouseEvent, ReactNode, useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { PopoverMenu, PopoverMenuItem } from '@ui/components/PopoverMenu/PopoverMenu';

import styles from './DropdownWithPopover.module.css';

type Props<T extends string> = {
  className?: string;
  options: {
    value?: T;
    label: ReactNode;
    disabled?: boolean;
  }[];
  placeholder?: ReactNode;
  onChange: (newValue?: T) => void;
  value?: T | null;
  valuePrefix?: ReactNode;
  flat?: boolean;
  disabled?: boolean;
};

export function DropdownWithPopover<T extends string>({
  className,
  options,
  placeholder,
  onChange,
  value,
  valuePrefix,
  flat,
  disabled,
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);
  const onClose = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      setIsOpen(false);
      e?.stopPropagation();
    },
    [setIsOpen],
  );

  const handleOptionClick = useCallback(
    (newValue?: T, e?: MouseEvent<HTMLElement>) => {
      onClose(e);
      onChange(newValue);
    },
    [onChange, onClose],
  );

  const currentOption = options.find((o) => o.value === value);

  return (
    <div
      className={cx(
        styles.root,
        {
          [styles.disabled]: disabled,
          [styles.focus]: isOpen,
          [styles.flat]: flat,
        },
        className,
      )}
      onClick={onOpen}
    >
      <div className={styles.value}>
        {valuePrefix}
        {currentOption?.label || placeholder}
      </div>

      <PopoverMenu
        className={styles.popoverWrapper}
        containerClassName={styles.popover}
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton
      >
        {options.map((o) => (
          <DropdownOption
            key={o.value}
            {...o}
            onChange={handleOptionClick}
            isSelected={value === o.value}
          />
        ))}
      </PopoverMenu>
    </div>
  );
}

type DropdownOptionProps<T extends string> = {
  onChange: (newValue?: T, e?: MouseEvent<HTMLElement>) => void;
  value?: T;
  label: ReactNode;
  isSelected: boolean;
  disabled?: boolean;
};
function DropdownOption<T extends string>({
  onChange,
  value,
  label,
  isSelected,
  disabled,
}: DropdownOptionProps<T>) {
  const handleChange = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      onChange(value, e);
    },
    [onChange, value],
  );

  return (
    <PopoverMenuItem
      className={cx(styles.option, { [styles.disabled]: disabled })}
      onClick={handleChange}
      isSelected={isSelected}
    >
      {label}
    </PopoverMenuItem>
  );
}
